import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth, dashboardPath, formatApiErrorDetail } from "../context/AuthContext";
import api from "../lib/api";
import { VEHICLE_TYPES } from "../lib/constants";
import { ShoppingCart, Store, Truck, Building2, Lock } from "lucide-react";

const ROLES = [
  { id: "buyer", label: "Buyer", icon: ShoppingCart, desc: "Browse & send inquiries" },
  { id: "seller", label: "Supplier", icon: Store, desc: "List stock & post jobs (needs approval)" },
  { id: "company", label: "Company", icon: Building2, desc: "Post transport jobs (needs approval)" },
  { id: "driver", label: "Transporter", icon: Truck, desc: "Sprinter, van & truck drivers (needs approval)" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("buyer");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", phone: "", whatsapp: "", vehicle_type: "", service_areas: "", routes: "", beta_code: "" });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ public_registration_enabled: false, private_beta: true }));
  }, []);

  const betaGated = settings && !settings.public_registration_enabled;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({ ...form, role });
      if (role === "buyer") toast.success("Account created");
      else toast.success("Account created — pending admin approval");
      navigate(dashboardPath(user.role));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("pending_role", role);
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white grid-noise">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-12">
        <div className="bg-white border border-foreground p-6 sm:p-8">
          <span className="label-caps text-primary">Join WILTOM TradeLink</span>
          <h1 className="font-display font-bold text-3xl tracking-tight mt-2 mb-6">Create account</h1>

          {betaGated && (
            <div className="border-2 border-accent bg-accent/10 p-4 mb-6 flex items-start gap-3" data-testid="beta-gate-notice">
              <Lock className="h-5 w-5 text-foreground shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Private beta — invitation only</p>
                <p className="text-xs text-muted-foreground mt-1">Public registration isn't open yet. Enter your beta access code to create a test account. Live business transactions open after legal & business registration checks are complete.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
            {ROLES.map((r) => (
              <button key={r.id} type="button" onClick={() => setRole(r.id)} data-testid={`role-${r.id}`}
                className={`p-3 border text-left transition-colors ${role === r.id ? "border-primary bg-primary/5" : "border-border hover:border-foreground"}`}>
                <r.icon className={`h-5 w-5 mb-2 ${role === r.id ? "text-primary" : "text-muted-foreground"}`} />
                <div className="font-medium text-sm">{r.label}</div>
                <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{r.desc}</div>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-caps">{role === "buyer" ? "Full name" : "Contact / driver name"}</Label>
              <Input value={form.name} onChange={upd("name")} required className="rounded-none mt-2 h-11" data-testid="register-name" />
            </div>
            {role !== "buyer" && (
              <div>
                <Label className="label-caps">Company name</Label>
                <Input value={form.company_name} onChange={upd("company_name")} className="rounded-none mt-2 h-11" data-testid="register-company" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps">Email</Label>
                <Input type="email" value={form.email} onChange={upd("email")} required className="rounded-none mt-2 h-11" data-testid="register-email" />
              </div>
              <div>
                <Label className="label-caps">Phone</Label>
                <Input value={form.phone} onChange={upd("phone")} className="rounded-none mt-2 h-11" data-testid="register-phone" />
              </div>
            </div>
            {role !== "buyer" && (
              <div>
                <Label className="label-caps">WhatsApp number</Label>
                <Input value={form.whatsapp} onChange={upd("whatsapp")} placeholder="+44…" className="rounded-none mt-2 h-11" data-testid="register-whatsapp" />
              </div>
            )}
            {role === "driver" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-caps">Vehicle type</Label>
                    <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                      <SelectTrigger className="rounded-none mt-2 h-11" data-testid="register-vehicle"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-caps">Service areas</Label>
                    <Input value={form.service_areas} onChange={upd("service_areas")} placeholder="e.g. DE, NL, BE" className="rounded-none mt-2 h-11" data-testid="register-areas" />
                  </div>
                </div>
                <div>
                  <Label className="label-caps">Available routes (optional)</Label>
                  <Input value={form.routes} onChange={upd("routes")} placeholder="e.g. Berlin ↔ Rotterdam" className="rounded-none mt-2 h-11" data-testid="register-routes" />
                </div>
                <p className="text-xs text-muted-foreground border border-accent bg-accent/10 p-2">
                  After registering, upload your insurance & vehicle documents from your dashboard. Admin must approve them before you can be assigned jobs.
                </p>
              </>
            )}
            <div>
              <Label className="label-caps">Password</Label>
              <Input type="password" value={form.password} onChange={upd("password")} required minLength={6}
                     className="rounded-none mt-2 h-11" data-testid="register-password" placeholder="Min 6 characters" />
            </div>
            {betaGated && (
              <div>
                <Label className="label-caps">Beta access code</Label>
                <Input value={form.beta_code} onChange={upd("beta_code")} required className="rounded-none mt-2 h-11" data-testid="register-beta-code" placeholder="Enter your private beta code" />
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="register-submit">
              {loading ? "Creating…" : betaGated ? "Create beta account" : "Create account"}
            </Button>
          </form>

          {!betaGated && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px bg-border flex-1" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
                <div className="h-px bg-border flex-1" />
              </div>
              <Button onClick={googleLogin} variant="outline" className="w-full rounded-none h-11 border-foreground" data-testid="register-google">
                Sign up with Google as {ROLES.find((r) => r.id === role)?.label}
              </Button>
            </>
          )}

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already registered? <Link to="/login" className="text-primary font-medium" data-testid="register-to-login">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
